// StudentDashboard.jsx (corrected and polished)
import React, { useEffect, useState } from 'react';
import { auth, db } from '../firebase';
import { doc, getDoc, collection, getDocs, updateDoc } from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import MockExamCard from '../components/MockExamCard';
import { getLastLocation } from '../utils/lastLocation';

const StudentDashboard = () => {
  const [userProgress, setUserProgress] = useState({});
  const [userName, setUserName] = useState('');
  const [expandedUnits, setExpandedUnits] = useState({});
  const [feedbackNotifications, setFeedbackNotifications] = useState([]);
  const [generalNotifications, setGeneralNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [mockCompleted, setMockCompleted] = useState(false);
  const [showHowToUse, setShowHowToUse] = useState(false);
  const [trialEnds, setTrialEnds] = useState(null);
const [plan, setPlan] = useState('');
const [lastLesson, setLastLesson] = useState(null);
  const navigate = useNavigate();


  const units = [
  { unitId: 1, unitTitle: 'Nature', badge: 'badge-nature-active.png', lessons: [1, 2, 3, 4] },
  { unitId: 2, unitTitle: 'Science', badge: 'badge-science-active.png', lessons: [5, 6, 7, 8] },
  { unitId: 3, unitTitle: 'Mind and Body', badge: 'badge-mind-active.png', lessons: [9, 10, 11, 12, 13] },
  { unitId: 4, unitTitle: 'Society', badge: 'badge-society-active.png', lessons: [14, 15, 16, 17, 18, 19] },
  { unitId: 5, unitTitle: 'Money', badge: 'badge-money-active.png', lessons: [20, 21, 22, 23, 24] },
  { unitId: 6, unitTitle: 'Government & Justice', badge: 'badge-justice-active.png', lessons: [25, 26, 27, 28, 29, 30, 31] },
  { unitId: 7, unitTitle: 'Relationships', badge: 'badge-relationships-active.png', lessons: [32, 33, 34, 35] },
  { unitId: 8, unitTitle: 'Culture', badge: 'badge-culture-active.png', lessons: [36, 37, 38, 39, 40] },
];

useEffect(() => {
  const last = getLastLocation();
  if (last && last.startsWith('/lesson/')) {
    setLastLesson(last);
  }
}, []);

  useEffect(() => {
    const user = auth.currentUser;
    if (user && !user.emailVerified) {
      alert('Please verify your email to access the dashboard.');
      navigate('/profile');
      return;
    }
  // 🔹 ADD — load last visited lesson for reminder


    const fetchUserData = async () => {
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          setUserName(data.name || 'Student');
          setUserProgress(data.lessons || {});
          setTrialEnds(data.trialEnds?.toMillis?.() || null);
setPlan(data.plan || 'trial');
 }

        const writingsRef = collection(db, 'users', user.uid, 'writings');
        const writingsSnap = await getDocs(writingsRef);
        const readyFeedbacks = [];
        writingsSnap.forEach((doc) => {
          const writing = doc.data();
          if (writing.feedbackStatus === 'ready') {
            readyFeedbacks.push({
              lessonId: writing.lessonId,
              feedbackPreview: writing.finalFeedback?.slice(0, 100) || 'Feedback available!',
            });
          }
        });
        setFeedbackNotifications(readyFeedbacks);

        const notifRef = collection(db, 'users', user.uid, 'notifications');
        const notifSnap = await getDocs(notifRef);
        const unreadNotifs = notifSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() })).filter((notif) => !notif.read);
        setGeneralNotifications(unreadNotifs);

        const mockAnswersRef = collection(db, 'users', user.uid, 'mockAnswers');
        const mockAnswersSnap = await getDocs(mockAnswersRef);
        const userMockDocs = mockAnswersSnap.docs;        

        const requiredParts = ['mock1_reading2', 'mock1_listening5', 'mock1_writing2'];
        const completedParts = userMockDocs.map(doc => doc.id);
        const isMockComplete = requiredParts.every(part => completedParts.includes(part));
        setMockCompleted(isMockComplete);

      } catch (error) {
        console.error('❌ Error fetching user data:', error);
      }
    };

    fetchUserData();
  }, [navigate]);

useEffect(() => {
  const now = Date.now();
  if ((plan === 'trial' && trialEnds < now) || plan === 'expired') {
    alert('Your subscription has ended. Please subscribe again to continue.');
    navigate('/subscribe');
  }
}, [trialEnds, plan, navigate]);


 const handleLessonClick = (lessonNum, unlocked) => {
  if (unlocked) {
    navigate(`/lesson/${lessonNum}/vocab`);
  } else {
    alert('This lesson is locked. Finish the previous lesson in this unit to unlock it.');
  }
};


const isLessonUnlocked = (lessonNum) => {
  // Find the unit that contains this lesson
  const unit = units.find((u) => u.lessons.includes(lessonNum));
  if (!unit) return false;

  const unitLessons = unit.lessons;
  const index = unitLessons.indexOf(lessonNum);

  // First lesson in a unit is always unlocked
  if (index === 0) return true;

  const prevLesson = unitLessons[index - 1];
  return !!userProgress[`lesson${prevLesson}`];
};


  const toggleUnit = (unitId) => {
    setExpandedUnits((prev) => ({ ...prev, [unitId]: !prev[unitId] }));
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      navigate('/signin');
    } catch (error) {
      console.error('❌ Error signing out:', error);
    }
  };

  const handleMarkNotificationsAsRead = async () => {
    const userId = auth.currentUser?.uid;
    if (!userId) return;
    try {
      const writingsRef = collection(db, 'users', userId, 'writings');
      const writingsSnap = await getDocs(writingsRef);

      const updates = writingsSnap.docs.map(async (docSnap) => {
        const data = docSnap.data();
        if (data.feedbackStatus === 'ready') {
          const writingRef = doc(db, 'users', userId, 'writings', docSnap.id);
          await updateDoc(writingRef, { feedbackStatus: 'viewed' });
        }
      });

      await Promise.all(updates);

      const notifRef = collection(db, 'users', userId, 'notifications');
      const notifSnap = await getDocs(notifRef);
      const notifUpdates = notifSnap.docs.map(async (docSnap) => {
        const data = docSnap.data();
        if (!data.read) {
          const notifDocRef = doc(db, 'users', userId, 'notifications', docSnap.id);
          await updateDoc(notifDocRef, { read: true });
        }
      });

      await Promise.all(notifUpdates);

      setFeedbackNotifications([]);
      setGeneralNotifications([]);
      setShowNotifications(false);

    } catch (error) {
      console.error('❌ Error marking notifications as read:', error);
    }
  };

  const totalLessons = 40;
  const completedLessons = Object.keys(userProgress).length;
  const progressPercent = Math.round((completedLessons / totalLessons) * 100);
  const unreadCount = feedbackNotifications.length + generalNotifications.length;
// 🔹 ADD
const prettyPath = (path) => {
  // /lesson/5/reading → "Lesson 5 – Reading"
  if (!path?.startsWith('/lesson/')) return '';
  const parts = path.split('/'); // ["", "lesson", "5", "reading"]
  const lessonNum = parts[2];
  const sectionRaw = parts[3] || '';
  const section = sectionRaw.replace(/-/g, ' ');
  return `Lesson ${lessonNum} – ${section.charAt(0).toUpperCase() + section.slice(1)}`;
};

  return (
    <div style={styles.dashboardContainer}>

<div style={styles.topRightButtons}>
    <button
  onClick={() => setShowHowToUse(true)}
  style={{
    padding: '10px 20px',
    backgroundColor: '#6c63ff',
    color: '#fff',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer'
  }}
>
  <span role="img" aria-label="information">ℹ️</span>

</button>

  {/* 🔔 Notification Bell */}
  <button
    onClick={() => setShowNotifications(!showNotifications)}
    style={styles.iconButton}
    aria-label={`Notifications (${unreadCount} unread)`}
  >
    <span role="img" aria-label="bell">🔔</span>
    {unreadCount > 0 && <span style={styles.notificationBadge}>{unreadCount}</span>}
  </button>


</div>

      {lastLesson && (
  <div style={styles.reminderBox}>
    <span> Welcome back! Last time you were at&nbsp;</span>
    <strong>{prettyPath(lastLesson)}</strong>.
    <button
      style={styles.resumeButton}
      onClick={() => navigate(lastLesson)}
      aria-label="Resume where you left off"
    >
      Resume
    </button>
  </div>
)}

      {/* 📨 Notifications Panel */}
      {showNotifications && (
        <div style={styles.notificationBox}>
          {unreadCount === 0 ? (
            <p>No new notifications!</p>
          ) : (
            <>
              {feedbackNotifications.length > 0 && (
                <>
                  <h4>Writings Feedback Ready</h4>
                  <ul>
                    {feedbackNotifications.map((fb, index) => (
                      <li key={index}><strong>{fb.lessonId}</strong>: {fb.feedbackPreview}...</li>
                    ))}
                  </ul>
                </>
              )}
              {generalNotifications.length > 0 && (
                <>
                  <h4>Teacher Feedback</h4>
                  <ul>
                    {generalNotifications.map((notif) => (
                 <li key={notif.id}>
                 {notif.target?.type === 'lesson' && notif.target.lessonId ? (
                   <button
                     style={{
                       background: 'none',
                       border: 'none',
                       color: 'blue',
                       textDecoration: 'underline',
                       cursor: 'pointer',
                       padding: 0
                     }}
                     onClick={async () => {
                       await updateDoc(doc(db, 'users', auth.currentUser.uid, 'notifications', notif.id), {
                         read: true
                       });
               
                       setGeneralNotifications((prev) =>
                         prev.filter((n) => n.id !== notif.id)
                       );
               
                       navigate(`/lesson/${notif.target.lessonId.replace('lesson', '')}/writing`);
                     }}
                   >
                     {notif.message}
                   </button>
                 ) : notif.target?.path ? (
                   <button
                     style={{
                       background: 'none',
                       border: 'none',
                       color: 'blue',
                       textDecoration: 'underline',
                       cursor: 'pointer',
                       padding: 0
                     }}
                     onClick={async () => {
                       await updateDoc(doc(db, 'users', auth.currentUser.uid, 'notifications', notif.id), {
                         read: true
                       });
               
                       setGeneralNotifications((prev) =>
                         prev.filter((n) => n.id !== notif.id)
                       );
               
                       navigate(notif.target.path);
                     }}
                   >
                     {notif.message}
                   </button>
                 ) : (
                   notif.message
                 )}
                 <br />
                 <small>{notif.date?.toDate().toLocaleString()}</small>
               </li>
               
                    ))}
                  </ul>
                </>
              )}
              <button style={styles.dismissButton} onClick={handleMarkNotificationsAsRead}>Mark all as Read</button>
            </>
          )}
        </div>
      )}

    {/* 📚 Header */}
<div style={styles.header}>
  <h1 style={styles.welcomeText}>Hey there, {userName || 'Student'}!</h1>
  {trialEnds && plan !== 'paid' && (
  <p style={{ color: '#b36b00', fontWeight: 'bold', marginBottom: '1rem' }}>
     Your trial ends in {Math.max(0, Math.ceil((trialEnds - Date.now()) / (1000 * 60 * 60 * 24)))} day{Math.ceil((trialEnds - Date.now()) / (1000 * 60 * 60 * 24)) !== 1 ? 's' : ''}.
  </p>
)}

  <div style={styles.headerButtons}>
    <button onClick={() => navigate('/profile')} style={styles.accountButton}>My Account</button>
    <button onClick={handleSignOut} style={styles.signOutButton}>Sign Out</button>
  </div>
</div>


   {/* 📈 Progress */}
<div style={styles.progressContainer}>
  <div style={styles.progressHeader}>
    <h3 style={styles.progressTitle}>Your Learning Progress</h3>
    <span style={styles.progressStats}>
      {completedLessons} / {totalLessons} lessons • {progressPercent}%
    </span>
  </div>

  <div style={styles.progressBarOuter}>
    <div
      style={{
        ...styles.progressBarInner,
        width: `${progressPercent}%`,
      }}
    />
  </div>

 <p style={styles.progressMessage}>
  {progressPercent === 100
     ? '🌟 You’ve completed every lesson — impressive effort!'
      : progressPercent >= 70
      ? 'So close! One final push to complete your course.'
      : progressPercent >= 30
      ? 'Your dedication is paying off — keep learning!'
      : 'Good start — consistency will take you far.'}
</p>
</div>


      {/* 🧩 Units + MockExamCard */}
      <div style={styles.unitGrid}>
       {units.map((unit) => {
  const completedCount = unit.lessons.filter((n) => userProgress[`lesson${n}`]).length;
  const totalCount = unit.lessons.length;
  const unitCompleted = completedCount === totalCount && totalCount > 0;
  const unitStarted = completedCount > 0 && !unitCompleted;
  const isExpanded = expandedUnits[unit.unitId];

  return (
    <div key={unit.unitId} style={styles.unitCard}>
      <div
        style={styles.unitHeader}
        onClick={() => toggleUnit(unit.unitId)}
        aria-label={`${unit.unitTitle} — ${completedCount}/${totalCount} lessons completed`}
      >
       <div style={styles.badgeWrap}>
  <div style={styles.badgeContainer}>
    <img
      src={`${process.env.PUBLIC_URL}/images/badges/${unit.badge}`}
      alt={unit.unitTitle}
      style={styles.badgeImage}/>
  </div>


          {/* Progress tag BELOW image */}
  <div style={styles.tagRow}>
    {unitCompleted ? (
      <span style={{ ...styles.statusTag, ...styles.statusComplete }}>✓ Complete</span>
    ) : unitStarted ? (
      <span style={{ ...styles.statusTag, ...styles.statusInProgress }}>In progress</span>
    ) : null}

    <span style={styles.progressTag}>
      {completedCount}/{totalCount} lessons
    </span>
  </div>
</div>

        <h2 style={styles.unitTitle}>{unit.unitTitle}</h2>
      </div>

      {isExpanded && (
        <ul style={styles.lessonList}>
          {unit.lessons.map((lessonNum) => {
            const lessonKey = `lesson${lessonNum}`;
            const completed = !!userProgress[lessonKey];
            const unlocked = isLessonUnlocked(lessonNum);

            return (
              <li key={lessonNum} style={styles.lessonListItem}>
                <button
                  onClick={() => handleLessonClick(lessonNum, unlocked)}
                  style={{
                    ...styles.lessonButton,
                    backgroundColor: completed ? '#28a745' : unlocked ? '#007bff' : '#6c757d',
                    cursor: unlocked ? 'pointer' : 'not-allowed',
                  }}
                  aria-label={`${completed ? 'Review' : 'Start'} Lesson ${lessonNum}`}
                >
                  {completed ? 'Review' : 'Start'} Lesson {lessonNum}
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <button onClick={() => toggleUnit(unit.unitId)} style={styles.expandButton}>
        {isExpanded ? 'Hide Lessons ▲' : 'Show Lessons ▼'}
      </button>
    </div>
  );
})}
{/* ✅ MockExamCard moved to the end */}
  <div style={styles.mockWrapper}>
    <MockExamCard mockCompleted={mockCompleted} />
  </div>

      </div>
      {showHowToUse && (
  <div style={styles.modalOverlay}>
    <div style={styles.modalContent}>
    <h2><span role="img" aria-label="book">📘</span> How to Use This App</h2>
<ul style={{ textAlign: 'left', lineHeight: '1.8', paddingLeft: '20px' }}>
  <li><span role="img" aria-label="puzzle">🧩</span> The app contains 8 Units, each with several lessons on 8 different most common topics in modern academic English.</li>
  <li><span role="img" aria-label="unlocked">🔓</span> You can start the first lesson in any unit, though, the recommeded order is 1 to 40, all the progress is saved in whichever order you do it</li>
  <li><span role="img" aria-label="locked">🔒</span> However, lessons are in order in the same topic. To unlock the next lesson in each unit, complete the previous one first.</li>
  <li><span role="img" aria-label="check">✅</span> Completed lessons turn green and become "Review" buttons, you can come back to revise or to complete. </li>
  <li><span role="img" aria-label="check">✅</span> You can unlock the next lesson without writing. You can do your writing tasks at any time. </li>
  <li><span role="img" aria-label="medal">🏅</span> When all lessons in a unit are done, the badge of that topical unit turns colourful.</li>
  <li><span role="img" aria-label="brain">🧠</span> Lessons include: Vocabulary, Practice, Reading, and Writing Integrated with Reading and Listening sections</li>
  <li><span role="img" aria-label="headphones">🎧</span> A full Mock Exam is available from the dashboard.</li>
  <li><span role="img" aria-label="chart">📊</span> Your teacher can track your progress and give feedback, you will see notification where the bell is, linked to your feedback</li>

      </ul>
      <button onClick={() => setShowHowToUse(false)} style={styles.closeButton}>
        Close
      </button>
    </div>
  </div>
)}

    </div>
  );
};

export default StudentDashboard;

const styles = {
  dashboardContainer: {
  padding: '20px',
  fontFamily: 'Georgia, serif',
  backgroundColor: '#f9f9f9',

  },
  notificationBox: {
    backgroundColor: '#fff3cd',
    color: '#856404',
    padding: '15px',
    borderRadius: '8px',
    marginBottom: '20px',
    border: '1px solid #ffeeba',
  },
  dismissButton: {
    backgroundColor: '#856404',
    color: '#fff',
    padding: '8px 12px',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    marginBottom: '20px'
  },
  headerButtons: {
    display: 'flex',
    gap: '10px',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
    marginTop: '10px'
  },
  
  welcomeText: {
    fontSize: '1.8rem'
  },
  signOutButton: {
    padding: '10px 20px',
    backgroundColor: 'blue',
    color: '#fff',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer'
  },
  accountButton: {
    padding: '10px 20px',
    backgroundColor: '#17a2b8',
    color: '#fff',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer'
  },
 progressContainer: {
  backgroundColor: '#fff',
  borderRadius: '10px',
  padding: '20px',
  boxShadow: '0 3px 8px rgba(0, 0, 0, 0.08)',
  marginBottom: '30px',
  textAlign: 'center',
  },
  progressHeader: {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexWrap: 'wrap',
  marginBottom: '10px',
},

progressTitle: {
  fontSize: '1.2rem',
  fontWeight: '600',
  color: '#333',
  margin: 0,
},

progressStats: {
  fontSize: '1rem',
  color: '#555',
  fontWeight: '500',
},

progressBarOuter: {
  backgroundColor: '#e6e6e6',
  height: '18px',
  borderRadius: '9px',
  overflow: 'hidden',
  position: 'relative',
},

progressBarInner: {
  height: '100%',
  background: 'linear-gradient(90deg, #6c63ff, #00bcd4)',
  borderRadius: '9px',
  transition: 'width 0.6s ease',
},

progressMessage: {
  marginTop: '12px',
  fontStyle: 'italic',
  color: '#444',
  fontSize: '0.95rem',
  },
 unitGrid: {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '20px',
  justifyContent: 'center'   // centers the last row
  },
 unitCard: {
  backgroundColor: '#ffffff',
  borderRadius: '10px',
  padding: '15px',
  boxShadow: '0 4px 6px rgba(49, 35, 184, 0.1)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  minHeight: '300px',
  transition: 'transform 0.3s',
  width: '280px'             // key: consistent tile width
  },
  unitHeader: {
    cursor: 'pointer',
    textAlign: 'center'
  },
  badgeImage: {
    width: '80%',
    height: 'auto',
    maxWidth: '200px',
    marginBottom: '10px'
  },
  unitTitle: {
    fontSize: '1.2rem',
    margin: '10px 0'
  },
  lessonList: {
    listStyleType: 'none',
    padding: 0,
    width: '100%'
  },
  lessonListItem: {
    marginBottom: '10px'
  },
  lessonButton: {
    width: '100%',
    padding: '10px',
    border: 'none',
    color: '#fff',
    borderRadius: '5px',
    fontSize: '14px'
  },
  expandButton: {
    marginTop: '10px',
    padding: '8px 15px',
    backgroundColor: 'light gray',
    color: '#fff',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer'
  },
  modalOverlay: {
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100vw',
  height: '100vh',
  backgroundColor: 'rgba(0, 0, 0, 0.6)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 1000
},
modalContent: {
  backgroundColor: '#fff',
  padding: '30px',
  borderRadius: '10px',
  maxWidth: '600px',
  width: '90%',
  boxShadow: '0 4px 10px rgba(0,0,0,0.2)',
  textAlign: 'center',
},
closeButton: {
  marginTop: '20px',
  padding: '10px 20px',
  backgroundColor: '#dc3545',
  color: '#fff',
  border: 'none',
  borderRadius: '5px',
  cursor: 'pointer'
},
badgeWrap: {
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '4px',
},

tagRow: {
  display: 'flex',
  justifyContent: 'center',
  gap: '6px',
  marginTop: '4px',
},

statusTag: {
  fontSize: '12px',
  padding: '4px 8px',
  borderRadius: '999px',
  color: '#fff',
  fontWeight: 600,
},

statusInProgress: { backgroundColor: '#f59e0b' },
statusComplete: { backgroundColor: '#16a34a' },

progressTag: {
  fontSize: '12px',
  padding: '4px 8px',
  borderRadius: '6px',
  color: '#111',
  backgroundColor: '#f1f1f1',
  fontWeight: 500,
},
badgeContainer: {
  width: '200px',         // consistent visible area for all badges
  height: '200px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
  margin: '0 auto',
},
mockWrapper: {
  width: '280px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'stretch',
  borderTop: '1px solid #eee',
  paddingTop: '20px',
  marginTop: '20px',
},

reminderBox: {
  background: '#f0f8ff',
  border: '1px solid #cce5ff',
  borderRadius: 12,
  padding: '10px 14px',
  margin: '16px auto 22px',
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  width: 'fit-content',
  maxWidth: '100%',
  fontSize: '16px'
},
resumeButton: {
  backgroundColor: '#007bff',
  color: '#fff',
  border: 'none',
  borderRadius: 8,
  padding: '6px 12px',
  cursor: 'pointer'
},

};

